// Client-side mirrors of the backend accept/dismiss side effects
// (apply_ai_fields_to_security / DismissAIFieldView), used to patch the
// store in place instead of refetching the whole cap table. SCAL-8959.

import { UNDERLYING_SECURITY_ALIAS } from 'common/constants/cap-table/capTable';
import { aiMetadataKeyOf, referenceFieldNameSet, SECURITY_FIELD_BY_AI_METADATA_KEY } from './aiFieldMap';
import { matchAiOption } from './resolveAiOptionValue';

type AnySecurity = Record<string, any>;

const refFieldName = (ref: any): string | undefined => ref?.field_name ?? ref?.fieldName;

// Soft-decline a reference list (SCAL-8976): flip is_accepted=false on every ref whose
// field name is in `fieldNames` - matched across every spelling the refs may carry
// (cell alias, model key, raw camelCase; see aiFieldMap) - KEEPING the rest of the
// list intact so a later full fetch reconciles to the same reversible shape.
// Shape-agnostic (snake_case security refs and camelCase cell refs both resolve via
// refFieldName), so the in-grid cell dismiss and the security patch share ONE
// decline implementation.
export const declineRefsForFields = (refs: any[] | undefined, fieldNames: string[]): any[] | undefined => {
  if (!Array.isArray(refs)) {
    return refs;
  }
  const names = referenceFieldNameSet(fieldNames);
  return refs.map(r => (names.has(refFieldName(r) as string) ? { ...r, is_accepted: false } : r));
};

// SCAL-9152: the SINGLE source for the cap-table grid's "color by reference" GREEN rule.
// A cell is GREEN (.cell-with-references) iff it carries an Excel/cap-table MAPPING
// reference. PDF citation references do NOT turn a cell green - they only badge the value
// viewer (see conditions.tsx; MappingReference has no is_accepted, so presence alone is
// the rule). Accept/dismiss therefore never force green: the cell stays green only while a
// mapping reference survives, so green always reflects the cell's reference state, not the
// review action.
export const cellHasReferenceGreen = (
  cell: { mappingReferences?: unknown[] | null } | null | undefined
): boolean => (cell?.mappingReferences?.length ?? 0) > 0;

// Mirror of backend review_status_service.is_pending (SCAL-8976): a field is
// pending when present and not yet settled. status, when present, is
// authoritative ('pending' only); otherwise fall back to is_accepted (truthy =>
// settled). This keeps soft-dismissed fields (is_accepted=false, status=dismissed)
// out of the pending set.
export const isPendingMeta = (meta: any): boolean => {
  if (meta == null) return false;
  if (meta.status) return meta.status === 'pending';
  return !meta.is_accepted;
};

export const pendingFieldNames = (security: AnySecurity): string[] => {
  const metadata = security?.ai_finding?.field_metadata ?? {};
  return Object.entries(metadata)
    .filter(([, meta]: [string, any]) => isPendingMeta(meta))
    .map(([fieldName]) => fieldName);
};

const UNDERLYING_SECURITY_KEY = aiMetadataKeyOf(UNDERLYING_SECURITY_ALIAS);

// Coercion contract: the AI metadata `value` is written onto the security field
// as-is (no type coercion, unlike the backend's cast_ai_field_value); the parser
// normalizes display and the next full fetch reconciles formatting. Two fields
// need TRANSLATION rather than coercion (aiFieldMap): their metadata lives under
// the MODEL key (underlying_security / security_type_id) while the grid reads the
// CELL-alias field (underlying_security_ref / security_type), and the
// underlying-security value arrives as a security NAME that must become the
// matching sibling's security_ref - the grid option id, matched via the shared
// resolver (exact, then case-insensitive trimmed) within `siblings`, the
// securities array being patched. An unresolved name writes NOTHING: the field
// still settles (the server already accepted it), but the raw name never lands
// on any field.
export const applyAcceptToSecurity = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => isPendingMeta(aiFinding.field_metadata[key]));
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  const next: AnySecurity = { ...security, ai_finding: { ...aiFinding, field_metadata: fieldMetadata } };
  targets.forEach(key => {
    const meta = fieldMetadata[key];
    fieldMetadata[key] = { ...meta, status: 'accepted', is_accepted: true };
    const value = key === UNDERLYING_SECURITY_KEY
      ? matchAiOption(
        siblings?.filter(s => s?.security_ref).map(s => ({ id: s.security_ref, name: s.name })),
        meta.value
      )?.id
      : meta.value;
    if (value !== undefined) next[SECURITY_FIELD_BY_AI_METADATA_KEY[key] ?? key] = value;
  });
  return next;
};

// SOFT decline (SCAL-8976): mirror the backend's reversible dismiss. The field
// metadata entry is KEPT (status='dismissed', is_accepted=false) instead of being
// removed, and matching citation refs are flipped is_accepted=false rather than
// dropped, so a later full fetch reconciles to the same soft shape.
export const applyDismissToSecurity = (security: AnySecurity, fieldNames: string[]): AnySecurity => {
  const aiFinding = security?.ai_finding;
  if (!aiFinding?.field_metadata) return security;
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => aiFinding.field_metadata[key] != null);
  if (!targets.length) return security;

  const fieldMetadata = { ...aiFinding.field_metadata };
  targets.forEach(key => {
    fieldMetadata[key] = { ...fieldMetadata[key], status: 'dismissed', is_accepted: false };
  });

  return {
    ...security,
    ai_finding: { ...aiFinding, field_metadata: fieldMetadata },
    citation_references: declineRefsForFields(security.citation_references, targets),
    mapping_references: declineRefsForFields(security.mapping_references, targets),
  };
};

// Reflect a bulk review response onto the securities store: apply EVERY settled
// field per security (accept or dismiss). Used by the cap-table drawer's bulk
// handler when the batch settled a security NAME, which triggers a full grid
// re-parse FROM the securities store - so the patch must cover all settled
// fields, not just the names: any field left out would be rebuilt as pending,
// visually un-settling the rest of the batch (the "bulk decline lost 73 of 80"
// regression). Both underlying helpers are idempotent on already-settled fields.
export const applySettledFieldsToSecurities = (
  securities: AnySecurity[],
  settledBySecurity: Map<number, string[]>,
  action: 'accept' | 'decline'
): AnySecurity[] =>
  securities.map(s => {
    const fields = s.id != null ? settledBySecurity.get(s.id) : undefined;
    if (!fields?.length) {
      return s;
    }
    // Accepts resolve underlying-security names against THIS array (siblings).
    return action === 'accept' ? applyAcceptToSecurity(s, fields, securities) : applyDismissToSecurity(s, fields);
  });

// Accept the field VALUE while REJECTING its citation (SCAL-9162). Citations are
// created INDEPENDENTLY of the value finding and can be wrong, so an analyst may
// keep the AI value yet unlink its (bad) citation. This is the third review action
// alongside applyAcceptToSecurity (accept value + keep citation) and
// applyDismissToSecurity (reject value + reject citation): it composes the SAME two
// primitives instead of duplicating them - applyAcceptToSecurity applies the value
// and stamps the field accepted, then the shared declineRefsForFields soft-unlinks
// the field's citation refs (is_accepted=false, reversible - T8). The MAPPING refs
// are deliberately left intact, so the green color-by-reference rule follows mapping
// presence (cellHasReferenceGreen), never the rejected citation. Returns the input
// unchanged when accept applied nothing (no pending target).
export const acceptValueRejectCitation = (
  security: AnySecurity,
  fieldNames: string[],
  siblings?: AnySecurity[]
): AnySecurity => {
  const metadata = security?.ai_finding?.field_metadata;
  if (!metadata) {
    return security;
  }
  // Only the PENDING fields are actually accepted (mirrors applyAcceptToSecurity's
  // internal target filter), so decline ONLY those fields' citations - NOT a sibling
  // field passed in the same call that had no pending suggestion. Mirrors
  // applyDismissToSecurity, which passes the pre-filtered `targets` to declineRefsForFields.
  const targets = fieldNames.map(aiMetadataKeyOf).filter(key => isPendingMeta(metadata[key]));
  if (!targets.length) {
    return security;
  }
  const accepted = applyAcceptToSecurity(security, targets, siblings);
  return {
    ...accepted,
    citation_references: declineRefsForFields(accepted.citation_references, targets),
  };
};

export const removeCitationFromSecurities = (securities: AnySecurity[], citationId: number): AnySecurity[] =>
  securities.map(s => {
    const refs = s.citation_references;
    if (!Array.isArray(refs)) return s;
    const next = refs.filter((r: any) => r?.id !== citationId);
    return next.length === refs.length ? s : { ...s, citation_references: next };
  });
